import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Typography, Button, Chip, CircularProgress,
  Alert, Card, CardContent, Dialog, IconButton,
  useTheme, useMediaQuery, Fade, Slide, Zoom
} from '@mui/material';
import {
  Phone, Email, LocationOn,
  Close as CloseIcon, ArrowBackIos, ArrowForwardIos,
  LocalParking, Tv, Balcony,
  Bed, Bathtub, Restaurant, Star, People, AttachMoney,
  PlayCircleOutlined, ZoomIn,
  LocalOffer, WaterDrop,
  AirportShuttle, Wc, KingBed, ChildFriendly, Weekend,
  Kitchen,
  AutoAwesome
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// ============= HARD-CODED VALUES =============
const GUESTHOUSE_EMAIL = 'workineh2152@gmail.com';
const GUESTHOUSE_LAT = 6.046392;
const GUESTHOUSE_LNG = 37.569146;

// ============= PHOTO DATA =============
const photoData = {
  '/images/1.jpg': { title: 'Master Bedroom', desc: 'Cozy queen bed with floral artwork', category: 'bedroom' },
  '/images/2.jpg': { title: 'Modern Bathroom', desc: 'Elegant sink with large mirror', category: 'bathroom' },
  '/images/3.jpg': { title: 'Guest Bedroom', desc: 'Spacious room with wardrobe', category: 'bedroom' },
  '/images/4.jpg': { title: 'Bedroom View', desc: 'Beautiful balcony view with greenery', category: 'bedroom' },
  '/images/5.jpg': { title: 'Living Room', desc: 'Comfortable sofa with entertainment setup', category: 'living' },
  '/images/6.jpg': { title: 'Modern Kitchen', desc: 'Fully equipped with all appliances', category: 'kitchen' },
  '/images/8.jpg': { title: 'Main Entrance', desc: 'Welcome to your home away from home', category: 'exterior' },
  '/images/9.jpg': { title: 'House Exterior', desc: 'Aerial view of the beautiful property', category: 'exterior' },
  '/images/10.jpg': { title: 'Bedroom Details', desc: 'Elegant curtains and natural lighting', category: 'bedroom' },
  '/images/11.jpg': { title: 'Property View', desc: 'Surrounded by lush green trees', category: 'exterior' },
  '/images/12.jpg': { title: 'Staircase', desc: 'Beautiful modern staircase', category: 'interior' },
  '/images/13.jpg': { title: 'Rain Shower', desc: 'Refreshing shower with window view', category: 'bathroom' },
  '/images/14.jpg': { title: 'Kids Room', desc: 'Fun and welcoming kids bedroom', category: 'bedroom' },
  '/images/16.jpg': { title: 'Living & Dining', desc: 'Open concept living space', category: 'living' },
  '/images/17.jpg': { title: 'Front Doors', desc: 'Grand wooden entrance', category: 'exterior' },
  '/images/19.jpg': { title: 'Kids Room', desc: 'Comfortable kids bedroom', category: 'bedroom' },
};

// ============= ANIMATED COUNTER =============
const AnimatedCounter = ({ value, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const increment = value / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start * 10) / 10);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [value, duration]);
  return <>{count}{suffix}</>;
};

const Home = () => {
  const [guesthouse, setGuesthouse] = useState(null);
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [filter, setFilter] = useState('all');
  const [videoError, setVideoError] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchData();

    const handleScroll = () => {
      const winScroll = document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      setScrollProgress((winScroll / height) * 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchData = async () => {
    try {
      const res = await api.get('/guesthouse');
      setGuesthouse(res.data.data.guesthouse);
      setRoom(res.data.data.rooms?.[0]);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ bgcolor: '#0a0e27' }}>
      <Box textAlign="center">
        <CircularProgress size={60} thickness={3} sx={{ color: '#ff6f00', mb: 3 }} />
        <Typography variant="body1" color="white" sx={{ opacity: 0.7 }}>
          Loading...
        </Typography>
      </Box>
    </Box>
  );
  if (error) return <Alert severity="error">{error}</Alert>;

  // Merge hard-coded 19.jpg into the images list
  const apiImages = guesthouse?.images || [];
  const images = apiImages.includes('/images/19.jpg')
    ? apiImages
    : [...apiImages, '/images/19.jpg'];

  const video = guesthouse?.video || '/videos/video.mp4';
  const heroImage = images[2] || images[0];

  const hiddenImages = ['/images/7.jpg', '/images/15.jpg'];
  const visibleImages = images.filter(img => !hiddenImages.includes(img));

  const filteredImages = filter === 'all'
    ? visibleImages
    : visibleImages.filter(img => photoData[img]?.category === filter);

  const openLightbox = (img) => {
    setLightbox(img);
    setLightboxIndex(filteredImages.indexOf(img));
  };

  const goNext = () => {
    const next = (lightboxIndex + 1) % filteredImages.length;
    setLightboxIndex(next);
    setLightbox(filteredImages[next]);
  };
  const goPrev = () => {
    const prev = (lightboxIndex - 1 + filteredImages.length) % filteredImages.length;
    setLightboxIndex(prev);
    setLightbox(filteredImages[prev]);
  };

  const amenityIcons = {
    'Continuous Water Supply': <WaterDrop />,
    'Free Parking': <LocalParking />,
    'Good Road Access': <AirportShuttle />,
    '3km from Airport': <AirportShuttle />,
    'Hot Water': <Bathtub />,
    'TV': <Tv />,
    'Full Kitchen': <Kitchen />,
    'Living Room': <Weekend />,
    'Dining Area': <Restaurant />,
    'Private Bathroom': <Bathtub />,
    'Balcony': <Balcony />,
    'Garden': <Weekend />,
  };

  const categories = [
    { value: 'all', label: 'All', emoji: '🏠' },
    { value: 'bedroom', label: 'Beds', emoji: '🛏️' },
    { value: 'living', label: 'Living', emoji: '🛋️' },
    { value: 'kitchen', label: 'Kitchen', emoji: '🍳' },
    { value: 'bathroom', label: 'Bath', emoji: '🚿' },
    { value: 'exterior', label: 'Outside', emoji: '🌳' },
    { value: 'interior', label: 'Inside', emoji: '✨' },
  ];

  return (
    <Box sx={{
      background: 'linear-gradient(180deg, #f5f0e8 0%, #faf8f3 30%, #f5f0e8 70%, #ede5d8 100%)',
      minHeight: '100vh',
      overflow: 'hidden',
      position: 'relative'
    }}>

      {/* SCROLL PROGRESS BAR */}
      <Box sx={{
        position: 'fixed',
        top: { xs: 56, md: 64 },
        left: 0,
        right: 0,
        height: 3,
        zIndex: 9999,
        bgcolor: 'rgba(0,0,0,0.05)'
      }}>
        <Box sx={{
          width: `${scrollProgress}%`,
          height: '100%',
          background: 'linear-gradient(90deg, #ff6f00, #1a237e)',
          transition: 'width 0.1s ease'
        }} />
      </Box>

      {/* HERO */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '85vh', md: '100vh' },
          minHeight: { xs: 500, md: 600 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          color: 'white'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundImage: `url(${heroImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            animation: 'slowZoom 20s ease-in-out infinite alternate',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(135deg, rgba(10,14,39,0.85) 0%, rgba(26,35,126,0.7) 50%, rgba(255,111,0,0.4) 100%)'
            }
          }}
        />

        <Box textAlign="center" px={{ xs: 2, md: 3 }} sx={{ maxWidth: 1000, position: 'relative', zIndex: 2 }}>
          <Fade in timeout={1000}>
            <Box>
              <Chip
                icon={<AutoAwesome sx={{ color: '#ffd54f !important', fontSize: '1rem' }} />}
                label={isMobile ? "LUXURY STAY" : "LUXURY · ARBA MINCH · ETHIOPIA"}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  mb: { xs: 2, md: 3 },
                  fontWeight: 700,
                  letterSpacing: { xs: 1, md: 2 },
                  px: { xs: 1, md: 2 },
                  py: { xs: 2, md: 2.5 },
                  fontSize: { xs: '0.7rem', md: '0.8rem' },
                  '& .MuiChip-label': { px: { xs: 1, md: 2 } }
                }}
              />
            </Box>
          </Fade>

          <Slide direction="up" in timeout={1200}>
            <Typography
              variant={isMobile ? 'h4' : 'h1'}
              fontWeight={800}
              gutterBottom
              sx={{
                textShadow: '0 8px 40px rgba(0,0,0,0.8)',
                letterSpacing: '-0.03em',
                lineHeight: 1.1,
                fontSize: { xs: '1.8rem', md: '4.5rem' },
                background: 'linear-gradient(135deg, #ffffff 0%, #ffd54f 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                mb: { xs: 2, md: 3 }
              }}
            >
              {guesthouse?.name}
            </Typography>
          </Slide>

          <Slide direction="up" in timeout={1400}>
            <Typography
              variant="body1"
              sx={{
                opacity: 0.95,
                mb: { xs: 3, md: 4 },
                fontWeight: 300,
                textShadow: '0 4px 20px rgba(0,0,0,0.7)',
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: { xs: '0.9rem', md: '1.25rem' }
              }}
            >
              {guesthouse?.description}
            </Typography>
          </Slide>

          <Fade in timeout={1600}>
            <Box display="flex" justifyContent="center" gap={{ xs: 1, md: 1.5 }} flexWrap="wrap" mb={{ xs: 3, md: 4 }}>
              <Chip
                icon={<AttachMoney sx={{ color: 'white !important' }} />}
                label="$60"
                sx={{
                  bgcolor: 'rgba(255,111,0,0.95)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: { xs: '0.75rem', md: '0.95rem' },
                  px: { xs: 0.5, md: 1 },
                  py: { xs: 2, md: 3 },
                  '& .MuiChip-label': { px: { xs: 1, md: 1.5 } }
                }}
              />
              <Chip
                icon={<LocalOffer sx={{ color: 'white !important' }} />}
                label={isMobile ? "5+ → $50" : "5+ nights → $50/night"}
                sx={{
                  bgcolor: 'rgba(76,175,80,0.95)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: { xs: '0.75rem', md: '0.95rem' },
                  px: { xs: 0.5, md: 1 },
                  py: { xs: 2, md: 3 },
                  '& .MuiChip-label': { px: { xs: 1, md: 1.5 } }
                }}
              />
              <Chip
                icon={<AirportShuttle sx={{ color: 'white !important' }} />}
                label={isMobile ? "3km Airport" : "3km from Airport"}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', md: '0.95rem' },
                  px: { xs: 0.5, md: 1 },
                  py: { xs: 2, md: 3 },
                  '& .MuiChip-label': { px: { xs: 1, md: 1.5 } }
                }}
              />
            </Box>
          </Fade>

          <Zoom in timeout={1800}>
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="center" gap={2} px={2}>
              <Button
                variant="contained"
                size={isMobile ? 'medium' : 'large'}
                onClick={() => navigate(`/book/${room?._id}`)}
                startIcon={<Bed />}
                fullWidth={isMobile}
                sx={{
                  background: 'linear-gradient(135deg, #ff6f00, #e65100)',
                  borderRadius: '50px',
                  px: { xs: 3, md: 5 },
                  py: { xs: 1.5, md: 2 },
                  fontWeight: 700,
                  fontSize: { xs: '0.95rem', md: '1.05rem' },
                  textTransform: 'none',
                  boxShadow: '0 15px 50px rgba(255,111,0,0.6)',
                  '&:hover': {
                    transform: 'translateY(-3px) scale(1.02)',
                    boxShadow: '0 20px 60px rgba(255,111,0,0.8)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Book Now
              </Button>
              <Button
                variant="outlined"
                size={isMobile ? 'medium' : 'large'}
                onClick={() => document.getElementById('video-section')?.scrollIntoView({ behavior: 'smooth' })}
                startIcon={<PlayCircleOutlined />}
                fullWidth={isMobile}
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  borderRadius: '50px',
                  px: { xs: 3, md: 4 },
                  py: { xs: 1.5, md: 2 },
                  fontWeight: 600,
                  textTransform: 'none',
                  backdropFilter: 'blur(20px)',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderWidth: 2,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.2)',
                    borderColor: 'white',
                    borderWidth: 2
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Watch Tour
              </Button>
            </Box>
          </Zoom>
        </Box>

        <style>
          {`
            @keyframes slowZoom {
              0% { transform: scale(1); }
              100% { transform: scale(1.08); }
            }
            @keyframes pulseBtn {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.05); }
            }
          `}
        </style>
      </Box>

      {/* STATS BAR */}
      <Box sx={{
        bgcolor: '#fdfbf6',
        boxShadow: '0 -10px 40px rgba(0,0,0,0.06)',
        position: 'relative',
        zIndex: 10
      }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 1, md: 4 } }}>
          <Grid container>
            {[
              { value: 60, label: 'Per Night', prefix: '$', icon: <AttachMoney /> },
              { value: 3, label: 'Bedrooms', suffix: '', icon: <KingBed /> },
              { value: 3, label: 'Km Airport', suffix: 'km', icon: <AirportShuttle /> },
              { value: 100, label: 'Capacity', suffix: '%', icon: <People /> },
            ].map((stat, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Box
                  sx={{
                    p: { xs: 1.5, md: 3 },
                    textAlign: 'center',
                    borderRight: { md: i < 3 ? '1px solid rgba(0,0,0,0.06)' : 'none' },
                    borderBottom: { xs: i < 2 ? '1px solid rgba(0,0,0,0.06)' : 'none', md: 'none' }
                  }}
                >
                  <Box sx={{ color: '#ff6f00', mb: 0.5, '& svg': { fontSize: { xs: 20, md: 24 } } }}>
                    {stat.icon}
                  </Box>
                  <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight={800} color="#1a237e">
                    {stat.prefix}<AnimatedCounter value={stat.value} suffix={stat.suffix} />
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* SPECIAL OFFER */}
      <Box sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
        <Card sx={{
          borderRadius: { xs: '16px', md: '20px' },
          background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
          color: 'white',
          boxShadow: '0 15px 40px rgba(76,175,80,0.3)',
          overflow: 'hidden'
        }}>
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1}>
              <LocalOffer sx={{ fontSize: { xs: 22, md: 32 } }} />
              <Typography variant={isMobile ? 'subtitle1' : 'h5'} fontWeight={800}>
                Special Discount!
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.95, fontSize: { xs: '0.85rem', md: '1rem' }, mb: 2 }}>
              Book <strong>5+ nights</strong> → pay only <strong>$50/night</strong> instead of $60
            </Typography>
            <Button
              variant="contained"
              fullWidth={isMobile}
              onClick={() => navigate(`/book/${room?._id}`)}
              sx={{
                bgcolor: 'white',
                color: '#2e7d32',
                borderRadius: '50px',
                px: 3,
                py: 1,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: { xs: '0.85rem', md: '0.95rem' }
              }}
            >
              Claim Discount
            </Button>
          </CardContent>
        </Card>
      </Box>

      {/* VIDEO */}
      {video && (
        <Box
          id="video-section"
          sx={{ py: { xs: 5, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: '#0a0e27' }}
        >
          <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
            <Box textAlign="center" mb={{ xs: 3, md: 5 }}>
              <Chip
                icon={<PlayCircleOutlined />}
                label="WATCH"
                sx={{
                  bgcolor: 'rgba(255,111,0,0.2)',
                  color: '#ff6f00',
                  fontWeight: 700,
                  mb: { xs: 2, md: 3 },
                  letterSpacing: 2,
                  fontSize: { xs: '0.7rem', md: '0.8rem' },
                  '& .MuiChip-icon': { color: '#ff6f00', fontSize: { xs: '1rem', md: '1.25rem' } }
                }}
              />
              <Typography variant={isMobile ? 'h5' : 'h3'} fontWeight={800} color="white" gutterBottom>
                Virtual House Tour
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', maxWidth: 600, mx: 'auto', fontSize: { xs: '0.85rem', md: '1rem' } }}>
                Take a cinematic walk through your future stay
              </Typography>
            </Box>

            <Box
              sx={{
                position: 'relative',
                borderRadius: { xs: '16px', md: '24px' },
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(255,111,0,0.3)'
              }}
            >
              {!videoError ? (
                <video
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  onError={() => setVideoError(true)}
                  style={{
                    width: '100%',
                    display: 'block',
                    maxHeight: isMobile ? 300 : 600,
                    background: '#000'
                  }}
                  poster={images[0]}
                >
                  <source src={video} type="video/mp4" />
                </video>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#1a1f3a' }}>
                  <Typography color="white">Video not available</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* GALLERY */}
      <Box sx={{ py: { xs: 5, md: 10 }, px: { xs: 1.5, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
        <Box textAlign="center" mb={{ xs: 3, md: 6 }}>
          <Typography
            variant="overline"
            sx={{ color: '#ff6f00', fontWeight: 700, letterSpacing: 3, fontSize: { xs: '0.75rem', md: '0.9rem' } }}
          >
            PHOTO GALLERY
          </Typography>
          <Typography variant={isMobile ? 'h5' : 'h3'} fontWeight={800} gutterBottom sx={{ mt: 1, color: '#1a237e' }}>
            Explore Every Detail
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: { xs: 2, md: 4 }, fontSize: { xs: '0.85rem', md: '1rem' } }}>
            {filteredImages.length} stunning photos of your home away from home
          </Typography>

          <Box sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', md: 'center' },
            gap: 1,
            flexWrap: { xs: 'nowrap', md: 'wrap' },
            overflowX: { xs: 'auto', md: 'visible' },
            pb: { xs: 1, md: 0 },
            px: { xs: 1, md: 0 },
            '&::-webkit-scrollbar': { display: 'none' }
          }}>
            {categories.map((cat) => (
              <Chip
                key={cat.value}
                label={`${cat.emoji} ${cat.label}`}
                onClick={() => setFilter(cat.value)}
                sx={{
                  fontWeight: 600,
                  px: { xs: 1, md: 2.5 },
                  py: { xs: 2, md: 3 },
                  fontSize: { xs: '0.75rem', md: '0.9rem' },
                  bgcolor: filter === cat.value ? '#1a237e' : '#fdfbf6',
                  color: filter === cat.value ? 'white' : '#1a237e',
                  border: '2px solid',
                  borderColor: filter === cat.value ? '#1a237e' : 'rgba(26,35,126,0.15)',
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: filter === cat.value ? '#0d47a1' : '#ffffff',
                    transform: 'translateY(-2px)'
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        <Grid container spacing={{ xs: 1, md: 2 }}>
          {filteredImages.map((img, i) => {
            const info = photoData[img] || { title: `Photo ${i + 1}`, desc: '', category: 'other' };
            const isFeature = !isMobile && (i === 0 || i === 6);
            return (
              <Grid item xs={12} sm={6} md={isFeature ? 6 : 3} key={i}>
                <Box
                  onClick={() => openLightbox(img)}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: { xs: '16px', md: '20px' },
                    overflow: 'hidden',
                    position: 'relative',
                    height: isMobile ? 220 : (isFeature ? 420 : 280),
                    transition: 'all 0.5s ease',
                    boxShadow: '0 4px 20px rgba(26,35,126,0.1)',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: '0 25px 60px rgba(26,35,126,0.35)',
                      '& .overlay': { opacity: 1 },
                      '& .image': { transform: 'scale(1.15)' },
                      '& .zoom-icon': { opacity: 1, transform: 'scale(1) rotate(0deg)' }
                    }
                  }}
                >
                  <img
                    className="image"
                    src={img}
                    alt={info.title}
                    loading="lazy"
                    style={{
                      width: '100%', height: '100%',
                      objectFit: 'cover', display: 'block',
                      transition: 'transform 0.8s ease'
                    }}
                  />

                  <Box
                    className="overlay"
                    sx={{
                      position: 'absolute', inset: 0,
                      background: 'linear-gradient(to top, rgba(10,14,39,0.95) 0%, transparent 100%)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      p: 2,
                      opacity: { xs: 1, md: 0 },
                      transition: 'opacity 0.5s ease'
                    }}
                  >
                    <Typography variant="body2" color="white" fontWeight={700}>
                      {info.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.7rem' }}>
                      {info.desc}
                    </Typography>
                  </Box>

                  <Box
                    className="zoom-icon"
                    sx={{
                      position: 'absolute', top: 12, right: 12,
                      width: 36, height: 36,
                      borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.9)',
                      display: { xs: 'none', md: 'flex' },
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transform: 'scale(0.5)',
                      transition: 'all 0.4s ease'
                    }}
                  >
                    <ZoomIn sx={{ color: '#1a237e', fontSize: 18 }} />
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* HOUSE FEATURES */}
      <Box sx={{ py: { xs: 5, md: 10 }, px: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
        <Box textAlign="center" mb={{ xs: 3, md: 6 }}>
          <Typography
            variant="overline"
            sx={{ color: '#ff6f00', fontWeight: 700, letterSpacing: 3, fontSize: { xs: '0.75rem', md: '0.9rem' } }}
          >
            THE HOUSE
          </Typography>
          <Typography variant={isMobile ? 'h5' : 'h3'} fontWeight={800} gutterBottom sx={{ mt: 1, color: '#1a237e' }}>
            What's Inside
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', fontSize: { xs: '0.85rem', md: '1rem' } }}>
            A G+1 luxury house with everything you need
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 2, md: 3 }}>
          {[
            { icon: <Weekend sx={{ fontSize: 28 }} />, title: 'Living Room', desc: 'Spacious living room with open kitchen' },
            { icon: <KingBed sx={{ fontSize: 28 }} />, title: 'Master Bedroom', desc: 'Comfortable master bedroom with queen bed' },
            { icon: <Bed sx={{ fontSize: 28 }} />, title: 'Guest Room', desc: 'Cozy guest bedroom for extra guests' },
            { icon: <ChildFriendly sx={{ fontSize: 28 }} />, title: 'Kids Room', desc: 'Fun and welcoming kids bedroom' },
            { icon: <Wc sx={{ fontSize: 28 }} />, title: 'Common Toilet', desc: 'Modern shared bathroom facilities' },
            { icon: <LocalParking sx={{ fontSize: 28 }} />, title: 'Parking Area', desc: 'Free parking for one car' },
            { icon: <WaterDrop sx={{ fontSize: 28 }} />, title: 'Continuous Water', desc: 'Reliable water supply 24/7' },
            { icon: <AirportShuttle sx={{ fontSize: 28 }} />, title: 'Good Road Access', desc: 'Just 3km from Arba Minch Airport' },
          ].map((feature, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card
                sx={{
                  p: { xs: 2, md: 3 },
                  borderRadius: '16px',
                  bgcolor: '#fdfbf6',
                  border: '2px solid transparent',
                  boxShadow: '0 4px 20px rgba(26,35,126,0.06)',
                  display: { xs: 'flex', md: 'block' },
                  alignItems: 'center',
                  gap: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'white',
                    borderColor: '#ff6f00',
                    transform: 'translateY(-5px)',
                    boxShadow: '0 15px 35px rgba(255,111,0,0.15)'
                  }
                }}
              >
                <Box
                  sx={{
                    width: { xs: 48, md: 64 },
                    height: { xs: 48, md: 64 },
                    borderRadius: '50%',
                    bgcolor: '#f5f0e8',
                    color: '#1a237e',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: { xs: 0, md: 'auto' },
                    mb: { xs: 0, md: 2 },
                    flexShrink: 0,
                    '& svg': { fontSize: { xs: 24, md: 32 } }
                  }}
                >
                  {feature.icon}
                </Box>
                <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                  <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight={700} color="#1a237e">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                    {feature.desc}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* AMENITIES */}
      <Box sx={{ py: { xs: 5, md: 10 }, px: { xs: 2, md: 4 } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box textAlign="center" mb={{ xs: 3, md: 6 }}>
            <Typography
              variant="overline"
              sx={{ color: '#ff6f00', fontWeight: 700, letterSpacing: 3, fontSize: { xs: '0.75rem', md: '0.9rem' } }}
            >
              AMENITIES
            </Typography>
            <Typography variant={isMobile ? 'h5' : 'h3'} fontWeight={800} gutterBottom sx={{ mt: 1, color: '#1a237e' }}>
              Everything You Need
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 1.5, md: 3 }}>
            {room?.amenities?.map((amenity, i) => (
              <Grid item xs={6} sm={4} md={3} key={i}>
                <Card
                  sx={{
                    p: { xs: 1.5, md: 3 },
                    textAlign: 'center',
                    borderRadius: '14px',
                    bgcolor: '#fdfbf6',
                    border: '2px solid transparent',
                    boxShadow: '0 4px 20px rgba(26,35,126,0.06)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'white',
                      borderColor: '#ff6f00',
                      transform: 'translateY(-5px)',
                      boxShadow: '0 15px 35px rgba(255,111,0,0.15)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 40, md: 56 },
                      height: { xs: 40, md: 56 },
                      borderRadius: '50%',
                      bgcolor: '#f5f0e8',
                      color: '#1a237e',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: { xs: 1, md: 2 },
                      '& svg': { fontSize: { xs: 20, md: 28 } }
                    }}
                  >
                    {amenityIcons[amenity] || <Star />}
                  </Box>
                  <Typography variant="body2" fontWeight={600} color="#1a237e" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                    {amenity}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* MAP */}
      <Box sx={{ py: { xs: 5, md: 10 }, px: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
        <Box textAlign="center" mb={{ xs: 3, md: 6 }}>
          <Typography
            variant="overline"
            sx={{ color: '#ff6f00', fontWeight: 700, letterSpacing: 3, fontSize: { xs: '0.75rem', md: '0.9rem' } }}
          >
            LOCATION
          </Typography>
          <Typography variant={isMobile ? 'h5' : 'h3'} fontWeight={800} gutterBottom sx={{ mt: 1, color: '#1a237e' }}>
            Find Us Here
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', fontSize: { xs: '0.85rem', md: '1rem' } }}>
            Abaya Sub-City, Adis Ketema Kebele, Nock-Site, Arba Minch
          </Typography>
        </Box>

        <Card sx={{
          borderRadius: { xs: '16px', md: '24px' },
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(26,35,126,0.15)'
        }}>
          <iframe
            title="Barkon Luxury Guesthouse Location"
            src={`https://www.google.com/maps?q=${GUESTHOUSE_LAT},${GUESTHOUSE_LNG}&z=17&output=embed`}
            width="100%"
            height={isMobile ? 300 : 450}
            style={{ border: 0, display: 'block' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Card>

        <Box textAlign="center" mt={{ xs: 2, md: 3 }}>
          <Button
            variant="outlined"
            href={`https://www.google.com/maps?q=${GUESTHOUSE_LAT},${GUESTHOUSE_LNG}`}
            target="_blank"
            startIcon={<LocationOn />}
            sx={{
              borderRadius: '50px',
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              borderWidth: 2,
              color: '#1a237e',
              borderColor: '#1a237e',
              '&:hover': { borderWidth: 2, bgcolor: 'rgba(26,35,126,0.05)' }
            }}
          >
            Open in Google Maps
          </Button>
        </Box>
      </Box>

      {/* STORY */}
      <Box sx={{ py: { xs: 5, md: 10 }, px: { xs: 2, md: 4 } }}>
        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
          <Card
            sx={{
              borderRadius: { xs: '20px', md: '32px' },
              background: 'linear-gradient(135deg, #fff8e1 0%, #ffe0b2 50%, #ffcc80 100%)',
              boxShadow: '0 30px 80px rgba(255,152,0,0.2)',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <Box sx={{
              position: 'absolute', top: -50, right: -50,
              width: 200, height: 200, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,111,0,0.2) 0%, transparent 70%)'
            }} />
            <CardContent sx={{ p: { xs: 3, md: 6 }, position: 'relative', zIndex: 2 }}>
              <Typography
                variant="overline"
                sx={{ color: '#e65100', fontWeight: 700, letterSpacing: 3, fontSize: { xs: '0.75rem', md: '0.9rem' } }}
              >
                OUR STORY
              </Typography>
              <Typography variant={isMobile ? 'h5' : 'h3'} fontWeight={800} gutterBottom sx={{ mt: 1, mb: { xs: 2, md: 3 } }}>
                ❤️ Made with Love
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: { xs: '0.9rem', md: '1.1rem' }, color: '#5d4037' }}>
                {guesthouse?.story}
              </Typography>
              <Box mt={3} display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 50, height: 50, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1a237e, #0d47a1)',
                    color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: '1.2rem'
                  }}
                >
                  B
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight={700} color="#1a237e">{guesthouse?.owner}</Typography>
                  <Typography variant="caption" color="text.secondary">Host & Owner</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* CONTACT */}
      <Box sx={{ px: { xs: 2, md: 4 }, pb: { xs: 4, md: 8 }, maxWidth: 1200, mx: 'auto' }}>
        <Card sx={{
          borderRadius: { xs: '20px', md: '32px' },
          background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 30px 80px rgba(26,35,126,0.3)'
        }}>
          <CardContent sx={{ p: { xs: 3, md: 6 }, position: 'relative', zIndex: 2 }}>
            <Typography
              variant="overline"
              sx={{ opacity: 0.7, fontWeight: 700, letterSpacing: 3, fontSize: { xs: '0.75rem', md: '0.9rem' } }}
            >
              GET IN TOUCH
            </Typography>
            <Typography variant={isMobile ? 'h5' : 'h3'} fontWeight={800} gutterBottom sx={{ mt: 1, mb: { xs: 3, md: 4 } }}>
              Contact Us
            </Typography>

            <Grid container spacing={{ xs: 1.5, md: 3 }} mb={{ xs: 2, md: 4 }}>
              <Grid item xs={12} md={4}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: { xs: 1.5, md: 2.5 },
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: '50px',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)', flexShrink: 0 }}>
                    <Phone sx={{ fontSize: { xs: 18, md: 24 } }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' } }}>
                      Amelmal Atinafu
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85, fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                      +251 911 524 667
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: { xs: 1.5, md: 2.5 },
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: '50px',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)', flexShrink: 0 }}>
                    <Phone sx={{ fontSize: { xs: 18, md: 24 } }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' } }}>
                      Workineh Abraham
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85, fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                      +251 926 162 152
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: { xs: 1.5, md: 2.5 },
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: '50px',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)', flexShrink: 0 }}>
                    <Email sx={{ fontSize: { xs: 18, md: 24 } }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' } }}>
                      Email Us
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85, wordBreak: 'break-all', fontSize: { xs: '0.7rem', md: '0.85rem' } }}>
                      {GUESTHOUSE_EMAIL}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{
              p: { xs: 2, md: 3 },
              bgcolor: 'rgba(255,255,255,0.08)',
              borderRadius: '14px',
              mb: { xs: 3, md: 4 },
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5
            }}>
              <LocationOn sx={{ fontSize: { xs: 20, md: 28 }, flexShrink: 0, mt: 0.3 }} />
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.7, fontSize: { xs: '0.7rem', md: '0.85rem' } }}>
                  Address
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}>
                  {guesthouse?.address}
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              size={isMobile ? 'medium' : 'large'}
              fullWidth
              onClick={() => navigate(`/book/${room?._id}`)}
              startIcon={<Bed />}
              sx={{
                background: 'linear-gradient(135deg, #ff6f00, #e65100)',
                color: 'white',
                borderRadius: '50px',
                px: { xs: 3, md: 5 },
                py: { xs: 1.5, md: 2 },
                fontWeight: 700,
                fontSize: { xs: '0.95rem', md: '1.05rem' },
                textTransform: 'none',
                boxShadow: '0 10px 40px rgba(255,111,0,0.5)',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 15px 50px rgba(255,111,0,0.7)'
                }
              }}
            >
              Book Now — $60/night
            </Button>
          </CardContent>
        </Card>
      </Box>

      {/* FLOATING BOOK BUTTON */}
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 16, md: 24 },
          right: { xs: 16, md: 24 },
          zIndex: 1000,
          animation: 'pulseBtn 2s infinite'
        }}
      >
        <Button
          variant="contained"
          onClick={() => navigate(`/book/${room?._id}`)}
          startIcon={<Bed sx={{ fontSize: { xs: 16, md: 20 } }} />}
          sx={{
            background: 'linear-gradient(135deg, #ff6f00, #e65100)',
            borderRadius: '50px',
            px: { xs: 2, md: 3 },
            py: { xs: 1.2, md: 1.8 },
            fontWeight: 700,
            textTransform: 'none',
            fontSize: { xs: '0.85rem', md: '1rem' },
            boxShadow: '0 15px 40px rgba(255,111,0,0.5)',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: '0 20px 50px rgba(255,111,0,0.7)'
            }
          }}
        >
          {isMobile ? 'Book' : 'Book Now'}
        </Button>
      </Box>

      {/* LIGHTBOX */}
      <Dialog
        open={!!lightbox}
        onClose={() => setLightbox(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'hidden', m: { xs: 1, md: 4 } } }}
      >
        <Box sx={{ position: 'relative', bgcolor: 'rgba(0,0,0,0.95)', borderRadius: { xs: '12px', md: '16px' }, overflow: 'hidden' }}>
          <IconButton
            onClick={() => setLightbox(null)}
            sx={{
              position: 'absolute', top: { xs: 8, md: 16 }, right: { xs: 8, md: 16 },
              bgcolor: 'rgba(255,255,255,0.15)', color: 'white', zIndex: 10,
              backdropFilter: 'blur(10px)',
              width: { xs: 36, md: 44 },
              height: { xs: 36, md: 44 },
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }}
          >
            <CloseIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
          </IconButton>

          <IconButton
            onClick={goPrev}
            sx={{
              position: 'absolute', top: '50%', left: { xs: 8, md: 16 },
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.15)', color: 'white', zIndex: 10,
              backdropFilter: 'blur(10px)',
              width: { xs: 36, md: 44 },
              height: { xs: 36, md: 44 },
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }}
          >
            <ArrowBackIos sx={{ fontSize: { xs: 18, md: 24 }, ml: { xs: 0.5, md: 1 } }} />
          </IconButton>

          <IconButton
            onClick={goNext}
            sx={{
              position: 'absolute', top: '50%', right: { xs: 8, md: 16 },
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.15)', color: 'white', zIndex: 10,
              backdropFilter: 'blur(10px)',
              width: { xs: 36, md: 44 },
              height: { xs: 36, md: 44 },
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }}
          >
            <ArrowForwardIos sx={{ fontSize: { xs: 18, md: 24 } }} />
          </IconButton>

          <img
            src={lightbox}
            alt="Full view"
            style={{
              width: '100%',
              maxHeight: isMobile ? '70vh' : '85vh',
              objectFit: 'contain',
              display: 'block'
            }}
          />

          {photoData[lightbox] && (
            <Box
              sx={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, transparent 100%)',
                color: 'white',
                p: { xs: 2, md: 4 },
                pt: { xs: 4, md: 8 }
              }}
            >
              <Typography variant={isMobile ? 'body1' : 'h5'} fontWeight={700}>
                {photoData[lightbox].title}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85, mt: 0.5, display: 'block' }}>
                {photoData[lightbox].desc}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.6, mt: 0.5, display: 'block' }}>
                {lightboxIndex + 1} / {filteredImages.length}
              </Typography>
            </Box>
          )}
        </Box>
      </Dialog>

    </Box>
  );
};

export default Home;